/**
 * Home (features #4, #5, #6). Dynamic category tabs, one realtime listener for
 * the category in view (minimizing reads), client-side search/filter, a
 * virtualized product list, offline/stale banner, and a last-sync footer.
 *
 * The active category is reflected in the URL (/category/:name) so push
 * deep-links to category/{name} land here on the right tab.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Product } from '@mitang/shared';
import { Banner } from '../../components/Banner';
import { relativeTime } from '../../lib/format';
import { PullToRefresh } from '../../components/PullToRefresh';
import { OfferButtons } from './OfferButtons';
import { ScratchBanner } from './ScratchBanner';
import { Spinner } from '../../components/Spinner';
import { CategoryTabs } from './CategoryTabs';
import { SearchFilterBar } from './SearchFilterBar';
import { SyncFooter } from './SyncFooter';
import { ProductList } from '../product/ProductList';
import { useCategories } from '../../hooks/useCategories';
import { useCategoryProducts } from '../../hooks/useCategoryProducts';
import { usePrices } from '../../hooks/usePrices';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useElementSize } from '../../hooks/useElementSize';
import {
  applyFilters,
  buildFacets,
  emptyFilters,
  pruneFilters,
  type ProductFilters,
} from '../product/search';
import { logActivity } from '../../lib/activity/activityLogger';
import { useWishlist } from '../../hooks/useWishlist';
import { HeartIcon } from '../../components/HeartButton';
import { setPresenceView } from '../../lib/presence';
import { ROUTES } from '../../routes/paths';
import { useOpenProduct, useProductOverlayOpen } from '../../routes/productOverlay';

export function HomeScreen(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ name?: string }>();
  const online = useNetworkStatus();

  const { categories, isLoading: catsLoading } = useCategories(true);
  const wishlist = useWishlist();
  const savedIds = useMemo(() => new Set(wishlist.ids), [wishlist.ids]);
  const [active, setActive] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProductFilters>(emptyFilters);
  const { ref: listAreaRef, height: listHeight } = useElementSize<HTMLDivElement>();

  // Sync active category with URL param + available categories.
  useEffect(() => {
    const fromUrl = params.name ? decodeURIComponent(params.name) : null;
    if (fromUrl && categories.includes(fromUrl)) {
      setActive(fromUrl);
    } else if (!active && categories.length > 0) {
      setActive(categories[0]);
    } else if (active && !categories.includes(active) && categories.length > 0) {
      setActive(categories[0]);
    }
  }, [params.name, categories, active]);

  // Log viewed_category and reset filters on category change.
  useEffect(() => {
    if (!active) return;
    setFilters(emptyFilters);
    logActivity('viewed_category', { category: active });
  }, [active]);

  // Reflect the browsed category on the Live monitor — again when a product
  // page drawn over this list closes (the list stays mounted underneath).
  const covered = useProductOverlayOpen();
  useEffect(() => {
    if (!active || covered) return;
    setPresenceView({ view: `category:${active}`, label: `Browsing ${active}` });
  }, [active, covered]);

  const { products, recentlyUpdated, isLoading: productsLoading, refreshing, freshAt } =
    useCategoryProducts(active);
  // Prices appear ONLY for users the admin allow-listed (see usePrices).
  const { byProduct: prices } = usePrices(active);

  // Facets CASCADE: each dropdown reflects the other active selections.
  const facets = useMemo(
    () => buildFacets(products, filters, active),
    [products, filters, active],
  );
  // If a selection became impossible (e.g. model not in the new series), drop it.
  useEffect(() => {
    const pruned = pruneFilters(products, filters, active);
    if (pruned !== filters) setFilters(pruned);
  }, [products, filters, active]);
  // SHEET ROW ORDER: v2 products carry rowIndex — list exactly as the sheet.
  const ordered = useMemo(() => {
    if (!products.some((p) => p.rowIndex !== undefined)) return products;
    return products.slice().sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
  }, [products]);
  const visible = useMemo(() => applyFilters(ordered, filters), [ordered, filters]);

  const onSelectCategory = (c: string) => {
    setActive(c);
    navigate(ROUTES.category(c), { replace: true });
  };

  const openProductById = useOpenProduct();
  const openProduct = (p: Product) => openProductById(p.id);

  return (
    <div className="flex min-h-full flex-1 flex-col bg-ground">
      <header className="flex items-start justify-between bg-ground px-4 pt-3.5">
        <div className="flex items-start gap-2.5">
          <img src="/logo-mark.png" alt="" className="mt-0.5 h-8 w-8 shrink-0" />
          <div>
            <div className="text-[18px] font-extrabold leading-tight tracking-tight text-ink">
              <span className="text-brand">HP</span> @ ANYWAY IT SOLUTIONS
            </div>
            <div className="pb-3 text-[11px] text-slate-400">Live dealer price list</div>
          </div>
        </div>
        <div className="mt-0.5 flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            aria-label={`Saved products${wishlist.count > 0 ? ` (${wishlist.count})` : ''}`}
            onClick={() => navigate(ROUTES.saved)}
            className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${
              wishlist.count > 0 ? 'bg-hot/10 text-hot' : 'bg-chipbg text-slate-500'
            }`}
          >
            <HeartIcon filled={wishlist.count > 0} size={14} />
            {wishlist.count > 0 ? wishlist.count : null}
          </button>
          <button
            type="button"
            aria-label="Profile"
            onClick={() => navigate(ROUTES.profile)}
            className="rounded-full bg-chipbg px-3.5 py-1.5 text-xs font-semibold text-slate-600"
          >
            Profile
          </button>
        </div>
      </header>

      {!online ? (
        <Banner tone="warning">You are offline — showing cached prices.</Banner>
      ) : null}

      <ScratchBanner />

      {catsLoading ? (
        <Spinner label="Loading catalog…" />
      ) : categories.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-10 text-sm text-slate-400">
          No products are available yet.
        </div>
      ) : (
        <>
          <CategoryTabs categories={categories} active={active} onSelect={onSelectCategory} />
          {active ? (
            <SearchFilterBar
              filters={filters}
              facets={facets}
              category={active}
              onChange={setFilters}
              onClear={() => setFilters(emptyFilters)}
            />
          ) : null}

          {active ? <OfferButtons category={active} /> : null}

          {/* Freshness: dealers can see at a glance that what they're quoting
              from is current (prices themselves are always fetched live). */}
          <div className="px-4 pb-0.5 pt-1 text-[10.5px] font-semibold">
            {refreshing ? (
              <span className="text-brand">↻ Refreshing…</span>
            ) : freshAt ? (
              <span className="text-drop">● Up to date · {relativeTime(freshAt)}</span>
            ) : null}
          </div>

          <PullToRefresh>
            <div ref={listAreaRef} className="relative flex-1">
              {productsLoading ? (
                <Spinner label="Loading products…" />
              ) : listHeight > 0 ? (
                <ProductList
                  products={visible}
                  recentlyUpdated={recentlyUpdated}
                  onOpen={openProduct}
                  height={listHeight}
                  prices={prices}
                  savedIds={savedIds}
                  onToggleSaved={wishlist.toggle}
                  scrollKey={active ? `home:${active}` : undefined}
                />
              ) : null}
            </div>
          </PullToRefresh>

          <SyncFooter category={active} />
        </>
      )}
    </div>
  );
}
